<header
    class="ai-chat-popup-header border-bottom px-2 py-2 d-flex align-items-center justify-content-between gap-2"
>
    <h2 class="h6 mb-0 text-truncate" id="ai-chat-popup-title">{{ activeThreadTitle() }}</h2>
    <div class="d-flex align-items-center gap-1 flex-shrink-0">
        <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            (click)="clearContext()"
            [disabled]="isStreaming()"
            aria-label="Clear conversation context"
            title="Clear context"
        >
            <mat-icon svgIcon="delete_sweep" class="icon-sm" aria-hidden="true" />
        </button>
        <button
            type="button"
            class="btn btn-sm btn-link text-decoration-none px-1"
            (click)="panel.close()"
            aria-label="Minimize AI Assistant"
        >
            <mat-icon svgIcon="minimize" class="icon-sm" aria-hidden="true" />
        </button>
    </div>
</header>

<div class="ai-chat-popup-thread-row border-bottom px-2 py-2 d-flex align-items-center gap-2">
    <div class="dropdown flex-grow-1 min-w-0" ngbDropdown>
        <button
            type="button"
            class="btn btn-sm btn-outline-secondary dropdown-toggle w-100 text-start text-truncate"
            ngbDropdownToggle
            [disabled]="isStreaming()"
            id="ai-chat-thread-dropdown"
        >
            {{ activeThreadTitle() }}
        </button>
        <div ngbDropdownMenu aria-labelledby="ai-chat-thread-dropdown" class="w-100">
            @for (thread of sortedThreads(); track thread.threadId) {
                <button
                    type="button"
                    ngbDropdownItem
                    [class.active]="thread.threadId === threads.activeThreadId()"
                    (click)="switchThread(thread)"
                >
                    <div class="text-truncate">{{ threads.threadTitle(thread) }}</div>
                </button>
            } @empty {
                <span class="dropdown-item-text small text-muted">No threads yet</span>
            }
        </div>
    </div>
    <button
        type="button"
        class="btn btn-sm btn-outline-primary flex-shrink-0"
        (click)="createThread()"
        [disabled]="isStreaming()"
        aria-label="New chat"
        title="New chat"
    >
        <mat-icon svgIcon="add" class="icon-sm" aria-hidden="true" />
    </button>
    <button
        type="button"
        class="btn btn-sm btn-outline-danger flex-shrink-0"
        (click)="deleteCurrentThread()"
        [disabled]="isStreaming() || threads.activeThreadId() === null"
        aria-label="Delete current chat"
        title="Delete chat"
    >
        <mat-icon svgIcon="delete" class="icon-sm" aria-hidden="true" />
    </button>
</div>

@if (utilizationPercent() > 0 || contextWarning() || isInputBlocked()) {
    <div class="ai-chat-popup-toolbar border-bottom px-2 py-1 d-flex align-items-center gap-2">
        <div
            class="progress flex-grow-1"
            role="progressbar"
            [attr.aria-valuenow]="utilizationPercent()"
            aria-valuemin="0"
            aria-valuemax="100"
            [attr.aria-label]="'Context utilization ' + utilizationPercent() + ' percent'"
        >
            <div
                class="progress-bar"
                [class.bg-warning]="contextWarning()"
                [class.bg-danger]="isInputBlocked()"
                [style.width.%]="utilizationPercent()"
            ></div>
        </div>
        <span class="small text-muted text-nowrap" aria-live="polite">
            {{ utilizationPercent() }}%
        </span>
    </div>
}

<div class="ai-chat-popup-body flex-grow-1 d-flex flex-column gap-2 p-2 overflow-hidden">
    @if (isInputBlocked()) {
        <div class="alert alert-danger py-1 small mb-0" role="alert">
            Context is nearly full. Clear context or start a new thread to continue.
        </div>
    } @else if (contextWarning()) {
        <div class="alert alert-warning py-1 small mb-0" role="status">
            Context is getting large. Clear context or start a new thread for better responses.
        </div>
    }

    @if (showStarters()) {
        <div class="d-flex flex-wrap gap-1">
            @for (prompt of starterPrompts; track prompt.label) {
                <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    (click)="sendPreset(prompt.message)"
                >
                    {{ prompt.label }}
                </button>
            }
        </div>
    }

    <div #messagesPane class="ai-chat-messages flex-grow-1" aria-live="polite">
        @for (line of messages(); track $index) {
            <div class="d-flex mb-2" [class.text-end]="line.role === 'user'">
                <div
                    class="text-wrap rounded-2 px-2 py-1"
                    [class.ms-auto]="line.role === 'user'"
                    [class.text-bg-primary]="line.role === 'user'"
                    [class.text-bg-light]="line.role === 'assistant'"
                    [class.ai-chat-assistant-bubble]="line.role === 'assistant'"
                    [class.ai-chat-user-bubble]="line.role === 'user'"
                >
                    @if (line.role === 'user') {
                        {{ line.text }}
                    } @else {
                        @let segments = segmentsFor(line.text);
                        @if (segments.thinking.length > 0) {
                            <div class="ai-chat-thinking">
                                <button
                                    type="button"
                                    class="btn btn-link btn-sm p-0 text-decoration-none d-flex align-items-center gap-1 text-muted"
                                    (click)="toggleThinking($index)"
                                    [attr.aria-expanded]="isThinkingOpen($index)"
                                >
                                    <mat-icon
                                        [svgIcon]="
                                            isThinkingOpen($index)
                                                ? 'arrow_drop_up'
                                                : 'arrow_drop_down'
                                        "
                                        class="icon-sm"
                                        aria-hidden="true"
                                    />
                                    Thinking
                                </button>
                                @if (isThinkingOpen($index)) {
                                    <pre
                                        class="ai-chat-thinking-body small mb-2 mt-1"
                                        [innerHTML]="responseHtml(segments.thinking)"
                                    ></pre>
                                }
                            </div>
                        }
                        @if (segments.response.length > 0) {
                            <div
                                class="ai-chat-md"
                                [innerHTML]="responseHtml(segments.response)"
                            ></div>
                        }
                    }
                </div>
            </div>
        }
        @if (streamingText().length > 0) {
            @let streamSeg = streamingSegments();
            @if (streamSeg.thinking.length > 0 || streamSeg.response.length > 0) {
                <div class="d-flex mb-2">
                    <div
                        class="text-wrap rounded-2 px-2 py-1 text-bg-light ai-chat-assistant-bubble"
                    >
                        @if (streamSeg.thinking.length > 0) {
                            <div class="ai-chat-thinking">
                                <button
                                    type="button"
                                    class="btn btn-link btn-sm p-0 text-decoration-none d-flex align-items-center gap-1 text-muted"
                                    (click)="toggleStreamingThinking()"
                                    [attr.aria-expanded]="streamingThinkingOpen()"
                                >
                                    <mat-icon
                                        [svgIcon]="
                                            streamingThinkingOpen()
                                                ? 'arrow_drop_up'
                                                : 'arrow_drop_down'
                                        "
                                        class="icon-sm"
                                        aria-hidden="true"
                                    />
                                    Thinking
                                </button>
                                @if (streamingThinkingOpen()) {
                                    <pre class="ai-chat-thinking-body small mb-2 mt-1">{{
                                        streamSeg.thinking
                                    }}</pre>
                                }
                            </div>
                        }
                        @if (streamSeg.response.length > 0) {
                            <div
                                class="ai-chat-md"
                                [innerHTML]="responseHtml(streamSeg.response)"
                            ></div>
                        }
                    </div>
                </div>
            }
        }
    </div>

    @if (toolChips().length > 0) {
        <div class="ai-chat-tools border rounded p-2">
            <button
                type="button"
                class="btn btn-link btn-sm p-0 text-decoration-none d-flex align-items-center gap-1"
                (click)="toggleToolPanel()"
                [attr.aria-expanded]="toolPanelOpen()"
            >
                <mat-icon
                    [svgIcon]="toolPanelOpen() ? 'arrow_drop_up' : 'arrow_drop_down'"
                    class="icon-sm"
                    aria-hidden="true"
                />
                Tool activity
            </button>
            @if (toolPanelOpen()) {
                <ul class="list-unstyled small mb-0 mt-2">
                    @for (chip of toolChips(); track chip.name) {
                        <li class="d-flex align-items-center gap-2">
                            @if (chip.status === 'running') {
                                <span
                                    class="spinner-border spinner-border-sm"
                                    aria-hidden="true"
                                ></span>
                            } @else {
                                <mat-icon
                                    svgIcon="check_circle"
                                    class="icon-sm text-success"
                                    aria-hidden="true"
                                />
                            }
                            <span>{{ chip.label }}</span>
                            @if (chip.durationMs) {
                                <span>{{ chip.durationMs }} ms</span>
                            }
                        </li>
                    }
                </ul>
            }
        </div>
    }

    @if (navigationActions().length > 0) {
        <div class="d-flex flex-wrap gap-1">
            @for (action of navigationActions(); track action.route) {
                <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    (click)="navigate(action)"
                >
                    {{ action.label }}
                </button>
            }
        </div>
    }

    <form class="ai-chat-composer d-flex gap-2 mt-auto align-items-end" (ngSubmit)="send()">
        <label class="visually-hidden" for="ai-chat-input">Message</label>
        <textarea
            #composerInput
            id="ai-chat-input"
            class="form-control form-control-sm"
            [(ngModel)]="draft"
            name="draft"
            rows="1"
            [disabled]="isStreaming() || isInputBlocked()"
            placeholder="Press Enter to send, Shift + Enter for new line ..."
            autocomplete="off"
            (keydown)="onComposerKeydown($event)"
            (input)="onComposerInput($event)"
        ></textarea>
        <button
            type="submit"
            class="btn btn-sm btn-primary"
            [disabled]="isStreaming() || isInputBlocked()"
        >
            Send
        </button>
    </form>
</div>
